import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import ProgressBar from './ProgressBar';
import Tooltip from './Tooltip';
import ErrorMessage from './ErrorMessage';
import { Book, FileText, Archive } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';

const ACCEPTED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
];
const ACCEPTED_EXTENSIONS = ['.pdf', '.docx', '.txt'];
const MAX_FILE_SIZE = 70 * 1024 * 1024; // 70MB

const UnifiedFileUpload = ({ onUploadSuccess }) => {
  const [books, setBooks] = useState([]);
  const [syllabus, setSyllabus] = useState(null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [success, setSuccess] = useState(false);
  const [dragActive, setDragActive] = useState(''); // '', 'books', 'syllabus', 'refPapers'
  const [response, setResponse] = useState(null);
  const [booksInputKey, setBooksInputKey] = useState(0);
  const [refPapersInputKey, setRefPapersInputKey] = useState(0);
  const [syllabusInputKey, setSyllabusInputKey] = useState(0);
  const [allFiles, setAllFiles] = useState([]);
  const [refPapersSelection, setRefPapersSelection] = useState({});
  const [allFilesInputKey, setAllFilesInputKey] = useState(0);

  const booksInputRef = useRef();
  const syllabusInputRef = useRef();
  const refPapersInputRef = useRef();
  const allFilesInputRef = useRef();

  const validateFiles = (files, allowMultiple = true) => {
    const fileArray = allowMultiple ? Array.from(files) : files ? [files] : [];
    for (const file of fileArray) {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        return 'Only PDF, DOCX, and TXT files are allowed.';
      }
      const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
      if (!ACCEPTED_EXTENSIONS.includes(ext)) {
        return 'File extension not allowed. Only .pdf, .docx, and .txt files are accepted.';
      }
      if (file.size > MAX_FILE_SIZE) {
        return 'File is too large. Maximum allowed size is 20MB.';
      }
      if (file.size === 0) {
        return 'File is empty. Please select a non-empty file.';
      }
    }
    return '';
  };

  // Drag-and-drop handlers for each field
  const handleDrag = (e, field) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(field);
    } else if (e.type === 'dragleave') {
      setDragActive('');
    }
  };

  const handleDrop = (e, field) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive('');
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      if (field === 'books') handleBooksChange({ target: { files: e.dataTransfer.files } });
      if (field === 'syllabus') handleSyllabusChange({ target: { files: e.dataTransfer.files } });
      if (field === 'refPapers') handleAllFilesChange({ target: { files: e.dataTransfer.files } });
    }
  };

  // Helper to add files to a list, avoiding duplicates by name
  const addFiles = (existing, newFiles) => {
    const names = new Set(existing.map(f => f.name));
    return [...existing, ...Array.from(newFiles).filter(f => !names.has(f.name))];
  };

  const handleBooksChange = (e) => {
    const files = e.target.files;
    const err = validateFiles(files, true);
    if (err) {
      setError(err);
      setBooks([]);
    } else {
      setError('');
      setBooks(prev => {
        const newBooks = addFiles(prev, files);
        console.log('Books after change:', newBooks.map(f => f.name));
        return newBooks;
      });
    }
    setBooksInputKey(k => k + 1);
    e.target.value = '';
  };

  const handleSyllabusChange = (e) => {
    const file = e.target.files[0];
    const err = validateFiles(file, false);
    if (err) {
      setError(err);
      setSyllabus(null);
    } else {
      setError('');
      setSyllabus(file);
    }
    setSyllabusInputKey(k => k + 1);
  };

  const handleAllFilesChange = (e) => {
    const files = Array.from(e.target.files);
    setAllFiles(prev => addFiles(prev, files));
    setAllFilesInputKey(k => k + 1);
    e.target.value = '';
  };

  // Toggle Reference Paper selection
  const toggleRefPaper = (fileName) => {
    setRefPapersSelection(sel => ({ ...sel, [fileName]: !sel[fileName] }));
  };

  // Remove file from Books
  const removeBook = (idx) => {
    setBooks(books => books.filter((_, i) => i !== idx));
    setBooksInputKey(k => k + 1);
  };

  // Remove Syllabus
  const removeSyllabus = () => {
    setSyllabus(null);
    setSyllabusInputKey(k => k + 1);
  };

  // Reference Papers are those checked in allFiles
  const refPapers = allFiles.filter(f => refPapersSelection[f.name]);

  // Remove file from allFiles (Reference Papers)
  const removeAllFile = (idx) => {
    setAllFiles(files => files.filter((_, i) => i !== idx));
    setAllFilesInputKey(k => k + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setUploadProgress(0);
    setResponse(null);
    if (books.length === 0 && !syllabus && refPapers.length === 0) {
      setError('Please upload at least one file: Books, Syllabus, or Reference Papers.');
      return;
    }
    const formData = new FormData();
    books.forEach((file) => formData.append('books', file));
    if (syllabus) formData.append('syllabus', syllabus);
    refPapers.forEach((file) => formData.append('refPapers', file));
    setUploading(true);
    try {
      const xhr = new XMLHttpRequest();
    xhr.open('POST', 'https://whitecurve.onrender.com/api/upload-multi');
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          setUploadProgress(Math.round((event.loaded * 100) / event.total));
        }
      };
      xhr.onload = () => {
        setUploading(false);
        if (xhr.status === 200) {
          setSuccess(true);
          const res = JSON.parse(xhr.responseText);
          setResponse(res);
          if (onUploadSuccess) onUploadSuccess(res);
        } else {
          setError('Upload failed: ' + xhr.statusText);
        }
      };
      xhr.onerror = () => {
        setUploading(false);
        setError('Upload failed.');
      };
      xhr.send(formData);
    } catch (err) {
      setUploading(false);
      setError('Upload failed.');
    }
  };

  // Helper to show selected file names
  const fileNames = (files) => files && files.length > 0 ? files.map(f => f.name).join(', ') : 'No files selected';

  // Require at least one of Books, Syllabus, or Reference Papers
  const canUpload = (books.length > 0 || syllabus || refPapers.length > 0) && !uploading;

  // Single card layout for sidebar
  return (
    <Card className="flex flex-col h-full w-full p-0">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Sources</CardTitle>
      </CardHeader>
      <form className="flex flex-col gap-4 flex-1 px-4 pb-4" onSubmit={handleSubmit} style={{position: 'relative', minHeight: '350px'}}>
        {/* Books/Study Material */}
        <motion.div
          whileHover={{ scale: 1.03, boxShadow: '0 4px 16px rgba(88,129,87,0.10)' }}
          whileFocus={{ scale: 1.03, boxShadow: '0 4px 16px rgba(88,129,87,0.13)' }}
          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          className={`flex items-center gap-2 border border-gray-200 rounded-md px-2 py-1 cursor-pointer text-xs ${dragActive==='books' ? 'bg-[#F6FAF6] border-[#588157]' : 'bg-white'}`}
          onDragEnter={e => handleDrag(e, 'books')}
          onDragOver={e => handleDrag(e, 'books')}
          onDragLeave={e => handleDrag(e, '')}
          onDrop={e => handleDrop(e, 'books')}
          onClick={() => booksInputRef.current && booksInputRef.current.click()}
          tabIndex={0}
          style={{ marginBottom: books.length > 0 ? '0.15em' : '0.3em', minHeight: 28, fontSize: '0.93em' }}
        >
          <Book size={16} color="#588157" />
          <span className="font-medium">Books</span>
          <input
            type="file"
            accept=".pdf,.docx,.txt"
            multiple
            ref={booksInputRef}
            className="hidden"
            onChange={handleBooksChange}
            disabled={uploading}
            name="books"
            key={booksInputKey}
          />
        </motion.div>
        {/* Books file list */}
        {books.length > 0 && (
          <ul className="mt-1 mb-2 space-y-1 max-h-20 overflow-y-auto pr-1" style={{minHeight:'0', maxHeight:'5.2em'}}>
            {books.map((file, idx) => (
              <li key={idx} className="flex items-center text-xs text-gray-700 filename-truncate-minimal">
                <span title={file.name} className="flex-1 filename-truncate-minimal">{file.name}</span>
                <button
                  type="button"
                  className="remove-cross-btn ml-1 text-gray-400 hover:text-red-500 text-base px-0.5 focus:outline-none"
                  onClick={e => { e.stopPropagation(); removeBook(idx); }}
                  aria-label="Remove file"
                >
                  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 6L14 14M14 6L6 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                </button>
              </li>
            ))}
          </ul>
        )}
        {/* Syllabus */}
        <motion.div
          whileHover={{ scale: 1.03, boxShadow: '0 4px 16px rgba(58,90,64,0.10)' }}
          whileFocus={{ scale: 1.03, boxShadow: '0 4px 16px rgba(58,90,64,0.13)' }}
          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          className={`flex items-center gap-2 border border-gray-200 rounded-md px-2 py-1 cursor-pointer text-xs ${dragActive==='syllabus' ? 'bg-[#F6FAF6] border-[#588157]' : 'bg-white'}`}
          onDragEnter={e => handleDrag(e, 'syllabus')}
          onDragOver={e => handleDrag(e, 'syllabus')}
          onDragLeave={e => handleDrag(e, '')}
          onDrop={e => handleDrop(e, 'syllabus')}
          onClick={() => syllabusInputRef.current && syllabusInputRef.current.click()}
          tabIndex={0}
          style={{ marginBottom: syllabus ? '0.15em' : '0.3em', minHeight: 28, fontSize: '0.93em' }}
        >
          <FileText size={16} color="#3A5A40" />
          <span className="font-medium">Syllabus</span>
          <input
            type="file"
            accept=".pdf,.docx,.txt"
            ref={syllabusInputRef}
            className="hidden"
            onChange={handleSyllabusChange}
            disabled={uploading}
            name="syllabus"
            key={syllabusInputKey}
          />
        </motion.div>
        {/* Syllabus file list */}
        {syllabus && (
          <div className="mt-1 mb-2 flex items-center text-xs text-gray-700 filename-truncate-minimal">
            <span title={syllabus.name} className="flex-1 filename-truncate-minimal">{syllabus.name}</span>
            <button
              type="button"
              className="remove-cross-btn ml-1 text-gray-400 hover:text-red-500 text-base px-0.5 focus:outline-none"
              onClick={e => { e.stopPropagation(); removeSyllabus(); }}
              aria-label="Remove file"
            >
              <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 6L14 14M14 6L6 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
            </button>
          </div>
        )}
        {/* Reference Papers */}
        <motion.div
          whileHover={{ scale: 1.03, boxShadow: '0 4px 16px rgba(163,177,138,0.10)' }}
          whileFocus={{ scale: 1.03, boxShadow: '0 4px 16px rgba(163,177,138,0.13)' }}
          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          className={`flex items-center gap-2 border border-gray-200 rounded-md px-2 py-1 cursor-pointer text-xs ${dragActive==='refPapers' ? 'bg-[#F6FAF6] border-[#588157]' : 'bg-white'}`}
          onDragEnter={e => handleDrag(e, 'refPapers')}
          onDragOver={e => handleDrag(e, 'refPapers')}
          onDragLeave={e => handleDrag(e, '')}
          onDrop={e => handleDrop(e, 'refPapers')}
          onClick={() => allFilesInputRef.current && allFilesInputRef.current.click()}
          tabIndex={0}
          style={{ marginBottom: refPapers.length > 0 ? '0.15em' : '0.3em', minHeight: 28, fontSize: '0.93em' }}
        >
          <Archive size={16} color="#A3B18A" />
          <span className="font-medium">Reference Papers</span>
          <input
            type="file"
            accept=".pdf,.docx,.txt"
            multiple
            ref={allFilesInputRef}
            className="hidden"
            onChange={handleAllFilesChange}
            disabled={uploading}
            name="refPapers"
            key={allFilesInputKey}
          />
        </motion.div>
        {/* Reference Papers file list with checkboxes */}
        {allFiles.length > 0 && (
          <ul className="mt-1 mb-2 space-y-1 max-h-20 overflow-y-auto pr-1" style={{minHeight:'0', maxHeight:'5.2em'}}>
            {allFiles.map((file, idx) => (
              <li key={file.name} className="flex items-center text-xs text-gray-700 filename-truncate-minimal">
                <input
                  type="checkbox"
                  checked={!!refPapersSelection[file.name]}
                  onChange={() => toggleRefPaper(file.name)}
                  className="mr-0.5"
                  aria-label="Mark as Reference Paper"
                />
                <span title={file.name} className="flex-1 filename-truncate-minimal" style={{maxWidth: '90px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', verticalAlign: 'middle'}}>
                  {file.name}
                </span>
                <button
                  type="button"
                  className="remove-cross-btn ml-1 text-gray-400 hover:text-red-500 text-base px-0.5 focus:outline-none"
                  onClick={e => { e.stopPropagation(); removeAllFile(idx); }}
                  aria-label="Remove file"
                >
                  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 6L14 14M14 6L6 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                </button>
              </li>
            ))}
          </ul>
        )}
        {/* Error and Progress */}
        {error && <ErrorMessage message={error} />}
        {uploading && <ProgressBar progress={uploadProgress} />}
        {/* Upload button fixed at the bottom */}
        <div style={{position: 'sticky', bottom: 0, left: 0, right: 0, background: 'inherit', zIndex: 10, paddingTop: '0.5em'}}>
          <button
            type="submit"
            className="bg-primary text-white rounded-lg px-4 py-2 font-semibold shadow hover:bg-primary/90 transition-colors disabled:opacity-60 w-full"
            disabled={!canUpload}
          >
            {uploading ? 'Uploading...' : 'Upload All'}
          </button>
        </div>
        {success && <span className="text-green-600 text-sm font-medium text-center">Upload successful!</span>}
      </form>
    </Card>
  );
};

export default UnifiedFileUpload; 
